import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';

import { HisUiService, HisAuthService, HisNavigationService, GenericValue } from 'hisng';

import { PATENT_STATE_REGISTERED, PATENT_STATE_PUBLISHED, PATENT_STATE_GRANTED } from 'app/fs/res/patent/model/constants';
import { Patent } from 'app/fs/res/patent/model/patent/patent';
import { PatentService } from 'app/fs/res/patent/service/patent/patent.service';
import { RightsUtils } from 'app/fs/res/common/utils/rights-utils';

@Component( {
    selector: 'res-patent-view-details',
    templateUrl: './patent-view-details.component.html',
} )
export class PatentViewDetailsComponent implements OnInit {

    private selectedPatentId: number;
    public patent: Patent = new Patent();
    public showEditButton = true;
    public hasEditRights = false;
    public showBackButton = false;
    public showSearchButton = true;
    public showParentLink = true;
    public showCitationDialog = false;
    public dateFormat: string;
    public patentFamily: Array<Patent> = [];
    patents: Patent[] = [];
    public patentForDetailView: Patent = new Patent();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public rights: RightsUtils,
        private hisUiService: HisUiService,
        public auth: HisAuthService,
        private navigationService: HisNavigationService,
        private patentService: PatentService ) {
    }

    ngOnInit() {
        this.dateFormat = this.hisUiService.translate( 'common.dateFormat' );
        this.route.params.subscribe( (params: Params) => {
            this.selectedPatentId = parseInt( params.patentId, 10 );
            this.patentService.canEditPatent( this.selectedPatentId ).subscribe( (r: boolean) => {
                this.hasEditRights = r;
            } );
            this.initView();
        } );
        // read parameters - back button and edit button
        this.route.queryParams.subscribe( (queryParams: Params) => {
            this.showEditButton = queryParams.showEditButton ? queryParams.showEditButton === 'true' : this.showEditButton;
            this.showBackButton = queryParams.showBackButton ? queryParams.showBackButton === 'true' : this.showBackButton;
            this.showSearchButton = queryParams.showSearchButton ? queryParams.showSearchButton === 'true' : this.showSearchButton;
        } );
    }


    setPatentFamily(): void {
        // get data for section patent family depending on patent type
        // (if is priority patent or patent with priority claim)
        let patentId = !this.patent.priorityPatent ? this.patent.id : this.patent.priorityPatent.id;

        if ( this.patent.priorityPatent && this.patent.priorityPatent.id > 0 ) {
            // add priorityPatent itself as well
            this.patentFamily.push( this.patent.priorityPatent );
        }

        // load child patents
        if ( patentId ) {
            this.patentService.getPatentFamily( patentId ).subscribe( (patents: Patent[]) => {
                patents.forEach( (p: Patent) => {
                    if ( p.id > 0 && p.id !== this.patent.id ) {
                        this.patentFamily.push( p );
                    }
                } );
            } );
        }
    }
    /*
     * Method to initialize the detail view of patent.
     * Reads the patent from the database and if available the parent patent as well.
     * Necessary to allow reload of view if patent id is changed. (e.g. when calling detail view of parent)
     */
    initView() {
        this.patentService.getPatentWithDetailsById( this.selectedPatentId ).subscribe( (p: Patent) => {
            this.patentFamily = [];
            this.patent = p;
            this.setPatentFamily();
            if ( this.isPriority() ) {
                this.patentForDetailView = this.patent;
            } else {
                this.patentForDetailView = this.patent.priorityPatent;
            }
            console.log( this.patent );
        } );
    }
    // return the url of the current window
    getUrl() {
        return window.location.href;
    }

    public isGranted(): boolean {
        if ( this.patent ) {
            if ( this.patent.status ) {
                if ( this.patent.status.hiskeyId === PATENT_STATE_GRANTED ) {
                    return true;
                }
            }
        }
        return false;
    }

    public isRegistered(): boolean {
        if ( this.patent ) {
            if ( this.patent.status ) {
                if ( this.patent.status.hiskeyId === PATENT_STATE_REGISTERED ) {
                    return true;
                }
            }
        }
        return false;
    }

    public isPublished(): boolean {
        if ( this.patent ) {
            if ( this.patent.status ) {
                if ( this.patent.status.hiskeyId === PATENT_STATE_PUBLISHED ) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * call edit-view of patent
     */
    public edit() {
        this.navigationService.setLastBackUrl(window.location.href); // remember last back URL
        this.router.navigate( [`/patent/edit/${this.patent.id}`], { queryParams: { showBackButton: true } } );
    }

    /* get all containing project Ids for searchConfiguration searchEntityIds */
    getProjectIdsAsString(): string {
        if ( this.patent.projects ) {
            return this.patent.projects.map( p => p.id ).join( ',' );
        }
        return undefined;
    }

    /* get all containing project Ids for searchConfiguration searchEntityIds */
    getPatentsIdsAsString(): string {
        if ( this.patents ) {
            return this.patents.map( p => p.id ).join( ',' );
        }
        return undefined;
    }

    isPriority(): boolean {
        return this.patent.priorityPatent == null ? true : false;
    }

    public getGenericValueIdForQueryParams(genericValue: GenericValue) {
        return '[\"' + genericValue.id + '\"]';
    }

    public getResearchAreaKdsfQueryParams(researchAreaKdsf: GenericValue): any {
        let queryParams = this.getBaseSearchQueryParams();
        /* eslint-disable-next-line @typescript-eslint/naming-convention, max-len */
        queryParams['COALESCE(Patent.researchAreaValues{researchAreaType="kdsf"}.id, Patent.priorityPatent.researchAreaValues{researchAreaType="kdsf"}.id)[]'] = this.getGenericValueIdForQueryParams(researchAreaKdsf);
        return queryParams;
    }

    public getResearchAreaQueryParams(researchArea: GenericValue): any {
        let queryParams = this.getBaseSearchQueryParams();
        /* eslint-disable-next-line @typescript-eslint/naming-convention, max-len */
        queryParams['COALESCE(Patent.researchAreaValues{researchAreaType="hochschuleigen"}.id, Patent.priorityPatent.researchAreaValues{researchAreaType="hochschuleigen"}.id)[]'] = this.getGenericValueIdForQueryParams(researchArea);
        return queryParams;
    }

    private getBaseSearchQueryParams(): any {
        return {
            configFile: 'fs/res/searchPatentHeadless.xml',
            pageTitle: 'fs.res.patent.search.header',
            tableTitle: 'fs.res.patent.search.found',
            extended: true,
            showBackButton:true,
            isNotActiveSearch:true
        };
    }

    public onBeforeNavigate() {
        this.navigationService.setLastBackUrl(window.location.href);
    }
}
